// src/components/survey/DynamicInput.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Radio,
  Checkbox,
  Button,
  Upload,
  notification,
} from "antd";
import { VideoIcon } from "lucide-react";
import { RxUpload } from "react-icons/rx";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { useGeneratePresignedUrlMutation } from "../../store/api/apiSlice";
import {
  uploadToPresignedUrl,
  generateFileName,
} from "../../utils/presignedUploader";
import { Frown, Meh, SmilePlus, Smile, SmileIcon, Star } from "lucide-react";
import {
  addUpload,
  updateUploadProgress,
  completeUpload,
  failUpload,
  cancelUpload,
  setAbortController,
} from "../../store/upload/uploadSlice";
import ChoiceLogoPicker from "./ChoiceLogoPicker";
import ChoicePopupPicker from "./ChoicePopupPicker";
import RadioListInput from "./RadioListInput";
import { isRadioListInputType, isRadioListRequiredEmpty } from "../../utils/radioList";
import { isValidSignatureName } from "../../utils/signature";
import { hasExplicitSliderSelection } from "../../utils/reportsHelper";

const MOBILE_BREAKPOINT = 768;

const toUploadListItem = (file, index) => {
  if (file instanceof File || file instanceof Blob) {
    return {
      uid: `${index}-${file.name || "file"}`,
      name: file.name || `File ${index + 1}`,
      status: "done",
      size: file.size,
      originFileObj: file,
    };
  }

  if (typeof file === "string") {
    const fileName = decodeURIComponent(
      file.split("?")[0].split("/").pop() || `File ${index + 1}`,
    );

    return {
      uid: `${index}-${file}`,
      name: fileName,
      status: "done",
      url: file,
    };
  }

  return null;
};

const FileUploadInput = ({ form, index, input, multiple = false }) => {
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    const existingValue = form.getFieldValue(["responses", index.toString()]);

    if (!existingValue || fileList.length) return;

    if (multiple) {
      const files = Array.isArray(existingValue)
        ? existingValue
        : [existingValue];
      setFileList(files.map(toUploadListItem).filter(Boolean));
      return;
    }

    setFileList([toUploadListItem(existingValue, 0)].filter(Boolean));
  }, []);

  const applyFileList = (nextList) => {
    const normalizedList =
      !multiple && nextList.length > 1 ? nextList.slice(-1) : nextList;

    setFileList(normalizedList);

    if (multiple) {
      const files = normalizedList
        .map((item) => item.originFileObj || item.url)
        .filter(Boolean);

      form.setFieldValue(
        ["responses", index.toString()],
        files.length ? files : null,
      );
      return;
    }

    if (normalizedList.length > 0) {
      form.setFieldValue(
        ["responses", index.toString()],
        normalizedList[0].originFileObj || normalizedList[0].url || null,
      );
      return;
    }

    form.setFieldValue(["responses", index.toString()], null);
  };

  return (
    <Form.Item name={["responses", index.toString()]}>
      <Upload.Dragger
        className="w-full p-4 rounded-xl"
        accept={input.file_type}
        fileList={fileList}
        multiple={multiple}
        beforeUpload={() => false}
        onChange={(info) => {
          applyFileList(info.fileList);
        }}
        onRemove={(file) => {
          applyFileList(fileList.filter((item) => item.uid !== file.uid));
          return true;
        }}
      >
        <p className="ant-upload-drag-icon">
          <RxUpload className="mx-auto text-2xl" />
        </p>
        <p className="ant-upload-text">
          Click or drag files to this area to upload
        </p>
      </Upload.Dragger>
    </Form.Item>
  );
};

const VideoRecordingSection = ({
  form,
  index,
  onVideoCapture,
  recording_limit = "00:00:30",
}) => {
  const recordingButtonRadiusClass = "!rounded-2xl";
  const dispatch = useDispatch();
  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();

  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [showRecording, setShowRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showLimitMessage, setShowLimitMessage] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const currentUploadIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  const mimeTypeRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const streamRef = useRef(null);

  const DEFAULT_MAX_RECORDING_SECONDS = 30;

  const convertTimeToSeconds = (timeString) => {
    if (!timeString || typeof timeString !== "string") {
      return DEFAULT_MAX_RECORDING_SECONDS;
    }

    const [hours = "00", minutes = "00", seconds = "00"] =
      timeString.split(":");
    const total =
      parseInt(hours, 10) * 3600 +
      parseInt(minutes, 10) * 60 +
      parseInt(seconds, 10);

    if (!Number.isFinite(total) || total <= 0) {
      return DEFAULT_MAX_RECORDING_SECONDS;
    }

    return total;
  };

  const maxRecordingTime = convertTimeToSeconds(recording_limit);

  const getReadableVideoUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    return url.split("?")[0];
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      if (mediaRecorderRef.current) {
        cleanupMediaStream();

        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setRecordingTime(0);
      setShowLimitMessage(false);

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          "Camera recording is not supported in this browser. Please use HTTPS or a modern browser.",
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 854, max: 854 },
          height: { ideal: 480, max: 480 },
          frameRate: { ideal: 24, max: 24 },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 32000,
        },
      });

      streamRef.current = stream;

      if (!videoRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("Video preview is not ready. Please try again.");
      }

      videoRef.current.removeAttribute("src");
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.playsInline = true;

      try {
        await videoRef.current.play();
      } catch (playError) {
        console.warn("Video preview autoplay failed:", playError);
      }

      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      );

      let options = {};
      let mimeType = "video/mp4";

      if (isSafari) {
        if (MediaRecorder.isTypeSupported("video/mp4")) {
          mimeType = "video/mp4";
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=h264")) {
          mimeType = "video/webm;codecs=h264";
        } else if (MediaRecorder.isTypeSupported("video/webm")) {
          mimeType = "video/webm";
        } else {
          console.warn(
            "Safari does not support MediaRecorder with the available formats",
          );
        }
      } else {
        if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
          mimeType = "video/webm;codecs=vp9";
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
          mimeType = "video/webm;codecs=vp8";
        } else if (MediaRecorder.isTypeSupported("video/webm")) {
          mimeType = "video/webm";
        } else if (MediaRecorder.isTypeSupported("video/mp4")) {
          mimeType = "video/mp4";
        }
      }

      if (mimeType && MediaRecorder.isTypeSupported(mimeType)) {
        options = {
          mimeType,
          videoBitsPerSecond: 1000000,
          audioBitsPerSecond: 64000,
        };

        if (mimeType === "video/mp4") {
          delete options.videoBitsPerSecond;
          delete options.audioBitsPerSecond;
        }
      }

      mimeTypeRef.current = mimeType || "video/webm";
      mediaRecorderRef.current = new MediaRecorder(
        stream,
        Object.keys(options).length > 0 ? options : undefined,
      );
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        notification.error({
          message: "Recording Error",
          description:
            event.error?.message || "Recording failed. Please try again.",
        });
        cleanupMediaStream();
        setIsRecording(false);
      };

      mediaRecorderRef.current.onstop = () => {
        setIsProcessingRecording(false);
        const recordedMimeType = mimeTypeRef.current || "video/webm";

        if (!chunksRef.current || chunksRef.current.length === 0) {
          console.error("No video chunks available");
          setIsRecording(false);
          setIsProcessingRecording(false);
          cleanupMediaStream();
          notification.error({
            message: "Recording Error",
            description: "No video data was captured. Please try again.",
          });

          return;
        }

        let extension = "mp4";

        if (recordedMimeType.includes("webm")) {
          extension = "webm";
        }

        try {
          const blob = new Blob(chunksRef.current, {
            type: recordedMimeType,
          });

          if (blob.size === 0) {
            console.error("Blob is empty");
            setIsRecording(false);
            setIsProcessingRecording(false);
            cleanupMediaStream();
            notification.error({
              message: "Recording Error",
              description: "Recorded video was empty. Please try again.",
            });

            return;
          }

          const videoData = {
            url: URL.createObjectURL(blob),
            blob,
            extension: extension,
            mimeType: recordedMimeType,
          };

          setRecordedVideo(videoData);

          stream.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          setIsRecording(false);

          if (timerRef.current) {
            clearInterval(timerRef.current);
          }

          setShowRecording(false);
          startUpload(videoData);
        } catch (error) {
          setIsRecording(false);
          setIsProcessingRecording(false);
          cleanupMediaStream();
          notification.error({
            message: "Recording Error",
            description:
              error?.message ||
              "Failed to process recording. Please try again.",
          });
        }
      };

      // Timeslice forces periodic chunks, improving reliability on some mobiles.
      mediaRecorderRef.current.start(250);
      setIsRecording(true);

      if (maxRecordingTime > 0) {
        autoStopTimerRef.current = setTimeout(() => {
          if (mediaRecorderRef.current?.state === "recording") {
            stopRecording();
            setShowLimitMessage(true);
          }

          autoStopTimerRef.current = null;
        }, maxRecordingTime * 1000);
      }
    } catch (err) {
      console.error("Error starting recording:", err);
      cleanupMediaStream();
      setIsRecording(false);
      notification.error({
        message: "Camera Error",
        description:
          err?.message ||
          "Failed to access camera or microphone. Please check your permissions.",
      });
    }
  };

  const startUpload = async (videoData) => {
    if (!videoData?.blob) return;

    if (currentUploadIdRef.current && abortControllerRef.current) {
      abortControllerRef.current.abort();
      dispatch(cancelUpload({ id: currentUploadIdRef.current }));
    }

    const uploadId = `upload_${index}_${Date.now()}`;
    currentUploadIdRef.current = uploadId;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsUploading(true);

    dispatch(
      addUpload({
        id: uploadId,
        metadata: {
          formIndex: index,
          type: "video",
        },
      }),
    );

    dispatch(setAbortController({ id: uploadId, abortController }));

    try {
      const contentType = videoData.mimeType || "video/webm";
      const fileName = generateFileName(contentType, "recording");

      const presignedResponse = await generatePresignedUrl({
        fileName,
        contentType,
        expiresIn: 600,
      }).unwrap();

      if (!presignedResponse?.url) {
        throw new Error("Failed to get presigned URL");
      }

      await uploadToPresignedUrl(
        presignedResponse.url,
        videoData.blob,
        contentType,
        (progress) => {
          dispatch(updateUploadProgress({ id: uploadId, progress }));
        },
        abortController.signal,
      );

      const serverUrl =
        presignedResponse.fileUrl || getReadableVideoUrl(presignedResponse.url);

      if (serverUrl) {
        dispatch(completeUpload({ id: uploadId, fileUrl: serverUrl }));

        setUploadedVideoUrl(serverUrl);

        form.setFieldValue(["responses", index.toString()], serverUrl);

        onVideoCapture(serverUrl);
      } else {
        throw new Error("No file URL returned from server");
      }
    } catch (error) {
      if (error.message === "Upload was aborted") {
        return;
      }

      dispatch(failUpload({ id: uploadId, error: error.message }));

      notification.error({
        message: "Upload Failed",
        description:
          error.message || "Failed to upload video. Please try again.",
      });
    } finally {
      setIsUploading(false);

      if (currentUploadIdRef.current === uploadId) {
        currentUploadIdRef.current = null;
        abortControllerRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    if (mediaRecorderRef.current?.state === "recording") {
      setIsProcessingRecording(true);
      try {
        mediaRecorderRef.current.requestData();
      } catch (e) {
        console.warn("Error requesting data:", e);
      }

      mediaRecorderRef.current.stop();
    }
  };

  const handleReRecord = async () => {
    if (currentUploadIdRef.current && abortControllerRef.current) {
      abortControllerRef.current.abort();
      dispatch(cancelUpload({ id: currentUploadIdRef.current }));
      currentUploadIdRef.current = null;
      abortControllerRef.current = null;
    }

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    cleanupMediaStream();

    mediaRecorderRef.current = null;
    chunksRef.current = [];

    setRecordedVideo(null);
    setUploadedVideoUrl(null);
    setShowLimitMessage(false);
    setIsUploading(false);
    setRecordingTime(0);

    await new Promise((resolve) => setTimeout(resolve, 200));

    startRecording();
  };

  const cleanupMediaStream = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === "recording") {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          console.warn("Error stopping MediaRecorder:", e);
        }
      }

      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.onerror = null;
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }

    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();

      tracks.forEach((track) => {
        track.stop();
        track.enabled = false;
      });

      videoRef.current.srcObject = null;
    }

    chunksRef.current = [];

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    setIsRecording(false);
  };

  const handleModalClose = () => {
    cleanupMediaStream();
    setShowRecording(false);
  };

  useEffect(() => {
    document.body.style.overflow = showRecording ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showRecording]);

  useEffect(() => {
    const savedValue = form.getFieldValue(["responses", index.toString()]);

    if (savedValue && typeof savedValue === "string" && !recordedVideo) {
      setUploadedVideoUrl(savedValue);
    }
  }, [form, index]);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxRecordingTime - 1) {
            clearInterval(timerRef.current);
            return maxRecordingTime;
          }

          return prev + 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [isRecording, maxRecordingTime]);

  useEffect(() => {
    return () => {
      cleanupMediaStream();
    };
  }, []);

  return (
    <div className="w-full space-y-4">
      <AnimatePresence>
        {showRecording ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black"
          >
            <div className="relative w-full h-full">
              <div className="w-full h-full">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  src={
                    isRecording
                      ? undefined
                      : uploadedVideoUrl || recordedVideo?.url || undefined
                  }
                />
                {!isRecording &&
                  !recordedVideo &&
                  !uploadedVideoUrl &&
                  !isProcessingRecording && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
                      <div className="text-center space-y-4 flex flex-col items-center">
                        <p className="text-white mb-4">
                          Maximum recording time: {formatTime(maxRecordingTime)}
                        </p>
                        <Button
                          onClick={startRecording}
                          type="primary"
                          icon={<VideoIcon className="mr-2 mt-.5" />}
                          className={`bg-[#0070BA] hover:bg-[#005ea6] inline-flex items-center justify-center whitespace-nowrap h-12 text-base px-6 mx-auto ${recordingButtonRadiusClass}`}
                        >
                          Start Recording
                        </Button>
                      </div>
                    </div>
                  )}
                {isRecording && (
                  <div className="absolute top-4 left-0 right-0 flex justify-center">
                    <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-full text-white">
                      Recording: {formatTime(recordingTime)} /{" "}
                      {formatTime(maxRecordingTime)}
                    </div>
                  </div>
                )}
                {showLimitMessage && (
                  <div className="absolute top-4 left-0 right-0 flex justify-center">
                    <div className="bg-red-500/90 backdrop-blur px-4 py-2 rounded-full text-white">
                      Recording limit reached
                    </div>
                  </div>
                )}
                {isRecording && (
                  <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4 px-4">
                    <Button
                      onClick={stopRecording}
                      type="primary"
                      className={`bg-[#0070BA] hover:bg-[#005ea6] h-8 flex-1 max-w-[200px] text-sm ${recordingButtonRadiusClass}`}
                    >
                      Finish Recording
                    </Button>
                  </div>
                )}
                {(recordedVideo || uploadedVideoUrl) && !isRecording && (
                  <div className="absolute bottom-10 left-0 right-0 flex justify-center gap-4 px-4">
                    <Button
                      onClick={handleReRecord}
                      className={`bg-white h-8 flex-1 max-w-[200px] text-sm ${recordingButtonRadiusClass}`}
                    >
                      Record Again
                    </Button>
                    <Button
                      onClick={() => setShowRecording(false)}
                      type="primary"
                      className={`bg-[#0070BA] hover:bg-[#005ea6] h-8 flex-1 max-w-[200px] text-sm ${recordingButtonRadiusClass}`}
                    >
                      Done
                    </Button>
                  </div>
                )}
                {!isRecording && (
                  <Button
                    onClick={handleModalClose}
                    className={`absolute top-4 right-4 bg-white/10 hover:bg-white/20 border-none text-white ${recordingButtonRadiusClass}`}
                  >
                    ✕
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <Button
                type="default"
                className="shadow-lg flex items-center justify-center px-8 py-2 rounded-2xl w-full !bg-[#0071E3] !text-white !border-transparent hover:!bg-[rgb(34_130_222)]"
                onClick={() => setShowRecording(true)}
              >
                <VideoIcon className="mr-2 h-5 w-5" />
                <span className="text-sm sm:text-base">Record a BSL Video</span>
              </Button>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DynamicInput = ({ input, form, index, preview }) => {
  const [sliderValue, setSliderValue] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const sliderInteractedRef = useRef(false);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [checkboxOtherValue, setCheckboxOtherValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT}px)`,
    );
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);
  const normalizeInputOptions = (rawOptions) => {
    const cleanOption = (option) =>
      typeof option === "string"
        ? option.replace(/^[\s"'[\]]+|[\s"'[\]]+$/g, "").trim()
        : option;

    const splitOptionText = (text) => {
      const trimmed = String(text ?? "").trim();
    
      if (!trimmed) return [];

      if (trimmed.includes("\n") || trimmed.includes("\r")) {
        return trimmed
          .split(/\r?\n/)
          .map((opt) => cleanOption(opt))
          .filter(Boolean);
      }

      if (trimmed.includes("|")) {
        return trimmed
          .split("|")
          .map((opt) => cleanOption(opt))
          .filter(Boolean);
      }

      const DECIMAL_COMMA = "\u0000";
      const protectedDecimals = trimmed.replace(
        /(\d),(\d)/g,
        `$1${DECIMAL_COMMA}$2`,
      );

      return protectedDecimals
        .split(",")
        .map((opt) =>
          cleanOption(String(opt).split(DECIMAL_COMMA).join(",")),
        )
        .filter(Boolean);
    };

    if (Array.isArray(rawOptions)) {
      if (
        rawOptions.length === 1 &&
        typeof rawOptions[0] === "string" &&
        (rawOptions[0].includes(",") ||
          rawOptions[0].includes("\n") ||
          rawOptions[0].includes("|"))
      ) {
        return splitOptionText(rawOptions[0]);
      }

      return rawOptions.map(cleanOption).filter(Boolean);
    }

    if (typeof rawOptions === "string") {
      const trimmed = rawOptions.trim();

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(cleanOption).filter(Boolean);
        }
      } catch {
        // fall through to list split
      }

      return splitOptionText(trimmed);
    }

    return [];
  };
  const inputOptions = normalizeInputOptions(input.input_options);
  const showChoiceLogos =
    (Boolean(input.show_logos) ||
      (Array.isArray(input.option_logos) &&
        input.option_logos.some(
          (url) => typeof url === "string" && url.trim() !== "",
        ))) &&
    (input.input_type === "radio" ||
      input.input_type === "checkbox" ||
      input.input_type === "dropdown");
  const choiceOptionLogos = Array.isArray(input.option_logos)
    ? input.option_logos
    : [];

  const renderChoiceOptionContent = (option, index) => {
    const logoUrl = showChoiceLogos ? choiceOptionLogos[index] : null;

    if (!logoUrl) {
      return option;
    }

    return (
      <span className="inline-flex items-center gap-3">
        <img
          src={logoUrl}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full border border-gray-200 object-cover bg-white shadow-sm"
        />
        <span>{option}</span>
      </span>
    );
  };

  const useLogoOptionPicker =
    showChoiceLogos &&
    inputOptions.length > 0 &&
    choiceOptionLogos.some(
      (url) => typeof url === "string" && url.trim() !== "",
    );

  const applyRadioSelection = (value) => {
    setSelectedOption(value);

    const isOther = value?.trim().toLowerCase() === "other";

    setShowOtherInput(isOther);

    if (isOther) {
      setOtherValue("");
      form.setFieldValue(["responses", index.toString()], {
        selection: value,
        otherValue: "",
      });
    } else {
      setOtherValue("");
      form.setFieldValue(["responses", index.toString()], value);
    }
  };

  const applyCheckboxSelection = (nextValues) => {
    const values = Array.isArray(nextValues) ? nextValues : [];
    setSelectedCheckboxes(values.length > 0 ? values : null);

    const hasOther = values.some(
      (v) => v?.trim().toLowerCase() === "other",
    );

    setShowOtherInput(hasOther);

    if (values.length === 0) {
      setCheckboxOtherValue("");
      form.setFieldValue(["responses", index.toString()], null);
      return;
    }

    if (hasOther) {
      form.setFieldValue(["responses", index.toString()], {
        selections: values,
        otherValue: checkboxOtherValue,
      });
    } else {
      setCheckboxOtherValue("");
      form.setFieldValue(["responses", index.toString()], values);
    }
  };

  const getDropdownFieldValue = () => {
    const fieldValue = form.getFieldValue(["responses", index.toString()]);

    if (fieldValue == null || fieldValue === "") {
      return null;
    }

    if (typeof fieldValue === "object") {
      return fieldValue.selection ?? null;
    }

    return fieldValue;
  };

  const applyDropdownSelection = (value) => {
    if (value?.trim().toLowerCase() === "other") {
      setShowOtherInput(true);
      form.setFieldValue(["responses", index.toString()], {
        selection: value,
        value: "",
      });
    } else {
      setShowOtherInput(false);
      setOtherValue("");
      form.setFieldValue(["responses", index.toString()], value);
    }
  };

  const clearDropdownSelection = () => {
    setShowOtherInput(false);
    setOtherValue("");
    setSelectedOption(null);
    form.setFieldValue(["responses", index.toString()], null);
  };

  const renderDropdownOtherInput = () =>
    showOtherInput ? (
      <Form.Item
        label={
          form.getFieldValue(["responses", index.toString()])?.selection ||
          "Other"
        }
        labelCol={{ span: 24 }}
        className="mb-0"
        name={["responses", `${index}_other`]}
        rules={[
          {
            required: preview ? false : (input.require ?? true),
            message: "Please specify a value",
          },
        ]}
      >
        <Input
          size="large"
          className="h-11 rounded-xl"
          placeholder="Please specify..."
          value={otherValue}
          onChange={(e) => {
            const value = e.target.value;
            setOtherValue(value);
            form.setFieldValue(["responses", index.toString()], {
              selection: form.getFieldValue([
                "responses",
                index.toString(),
              ])?.selection,
              value: value,
            });
          }}
        />
      </Form.Item>
    ) : null;

  const normalizeCheckboxValues = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.selections)) {
      return value.selections;
    }
    return [];
  };

  const getColorScheme = (value) => {
    const colors = {
      primary: "#0070BA",
      red: "#FF4D4F",
      orange: "#FF7A45",
      yellow: "#FFA940",
      green: "#73D13D",
    };

    if (value <= 20)
      return {
        color: colors.red,
        textColor: colors.red,
      };

    if (value <= 40)
      return {
        color: colors.orange,
        textColor: colors.orange,
      };

    if (value <= 60)
      return {
        color: colors.yellow,
        textColor: colors.yellow,
      };

    if (value <= 80)
      return {
        color: colors.primary,
        textColor: colors.primary,
      };

    return {
      color: colors.green,
      textColor: colors.green,
    };
  };

  const getSliderBackground = (value) => {
    const { color } = getColorScheme(value);

    return `linear-gradient(to right, ${color} ${value}%, #E5E7EB ${value}%)`;
  };

  const handleVideoCapture = async (blob) => {
    form.setFieldValue(["responses", index.toString()], blob);
  };

  const RatingOption = ({
    value,
    label,
    selected,
    onClick,
    index,
    showEmojis,
    showStars,
    show_only_emojis,
    totalOptions,
    isStarFilled,
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    const getIconSet = (total) => {
      const iconSets = {
        4: [
          { Icon: Frown, color: "#FF4D4F" },
          { Icon: Meh, color: "#FFA940" },
          { Icon: Smile, color: "#52C41A" },
          { Icon: SmilePlus, color: "#1890FF" },
        ],
        5: [
          { Icon: Frown, color: "#FF4D4F" },
          { Icon: Meh, color: "#FFA940" },
          { Icon: SmileIcon, color: "#FADB14" },
          { Icon: Smile, color: "#52C41A" },
          { Icon: SmilePlus, color: "#1890FF" },
        ],
        7: [
          { Icon: Frown, color: "#FF4D4F" },
          { Icon: Frown, color: "#FF7A45" },
          { Icon: Meh, color: "#FFA940" },
          { Icon: SmileIcon, color: "#FADB14" },
          { Icon: Smile, color: "#73D13D" },
          { Icon: Smile, color: "#52C41A" },
          { Icon: SmilePlus, color: "#1890FF" },
        ],
      };

      return iconSets[total] || iconSets["5"];
    };

    const getStarIconSet = (total) => {
      const yellowColor = "#FFD700";
      const starSets = {
        4: [
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
        ],
        5: [
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
        ],
        7: [
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
          { Icon: Star, color: yellowColor },
        ],
      };

      return starSets[total] || starSets["5"];
    };

    const scaleTotal = totalOptions?.toString() || "5";
    const iconSet = getIconSet(scaleTotal);
    const starSet = getStarIconSet(scaleTotal);

    let IconComponent = null;
    let iconColor = null;

    if (showStars) {
      IconComponent = starSet[index]?.Icon || Star;
      iconColor = isStarFilled ? starSet[index]?.color || "#FFD700" : "#B1B1B1";
    } else if (showEmojis) {
      IconComponent = iconSet[index]?.Icon;
      iconColor = iconSet[index]?.color;
    }

    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`relative ${
          !label ? "flex-none w-fit sm:w-auto sm:flex-1" : "flex-1 min-w-0"
        } p-1 pt-2 pb-2 flex flex-col justify-start items-center cursor-pointer rounded-lg transition-all ${
          !label
            ? "h-auto"
            : show_only_emojis
              ? "h-[56px] sm:h-[64px]"
              : "h-[84px]"
        }
        }`}
        style={
          selected
            ? {
                boxShadow: "none",
              }
            : undefined
        }
      >
        <div className="m-0 leading-none shrink-0">
          {showEmojis || showStars ? (
            <div
              className={`${
                showStars ? "w-8 h-8" : "w-6 h-6"
              } flex items-center justify-center leading-none transition-all [&>svg]:block [&>svg]:max-h-full [&>svg]:max-w-full ${
                selected ? "scale-110" : ""
              }`}
            >
              {IconComponent && (
                <IconComponent
                  size={showStars ? 32 : 24}
                  color={
                    showStars
                      ? isStarFilled
                        ? "rgb(244 181 4)"
                        : isHovered
                          ? "#5F6368"
                          : "#B1B1B1"
                      : selected
                        ? iconColor
                        : "#B1B1B1"
                  }
                  strokeWidth={
                    showStars ? (isStarFilled ? 1.5 : 2) : selected ? 2 : 1.5
                  }
                  fill={
                    showStars
                      ? isStarFilled
                        ? "rgb(244 181 4)"
                        : "none"
                      : "none"
                  }
                  className="block shrink-0 transition-all duration-200 cursor-pointer"
                />
              )}
            </div>
          ) : (
            <div
              style={{ padding: 3 }}
              className={`border border-2 rounded-full transition-all duration-200 ${
                selected ? "border-black scale-110" : "border-gray-300"
              }`}
            >
              <div
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  selected ? "bg-primary scale-110" : "bg-gray-300"
                }`}
              />
            </div>
          )}
        </div>
        {!show_only_emojis && !!label && (
          <span
            className={`text-[10px] text-center leading-tight font-medium transition-all duration-200 ${
              showStars ? "pt-1 pb-0 mt-0" : "py-2"
            }
            block mx-auto max-w-[90px] min-h-[2.1rem]
            ${
              selected
                ? "visible text-primary font-semibold"
                : "invisible sm:visible text-gray-500"
            }`}
            title={typeof label === "string" ? label : undefined}
          >
            <span
              style={{
                whiteSpace: "normal",
                overflowWrap: "normal",
                wordBreak: "normal",
              }}
            >
              {label}
            </span>
          </span>
        )}
      </div>
    );
  };

  const renderInput = () => {
    if (isRadioListInputType(input.input_type)) {
      return <RadioListInput input={input} disabled={preview} />;
    }

    switch (input.input_type) {
      case "text":
        return (
          <Input
            size="large"
            className="h-11 rounded-xl"
            placeholder="Type here..."
          />
        );
      case "paragraph":
        return (
          <Input.TextArea
            size="large"
            className="rounded-xl"
            rows={4}
            placeholder="Type here..."
          />
        );
      case "name":
        return (
          <Input
            size="large"
            className="rounded-xl"
            placeholder="Enter your name..."
          />
        );
      case "number":
        return (
          <Input
            type="number"
            size="large"
            className="h-11 rounded-xl"
            placeholder="Enter number"
          />
        );
      case "date":
        return <Input type="date" size="large" className="h-11 rounded-xl" />;
      case "email":
        return (
          <Input
            type="email"
            size="large"
            className="h-11 rounded-xl"
            placeholder="Enter your email address..."
            autoComplete="email"
          />
        );
      case "dropdown":
        if (useLogoOptionPicker) {
          return (
            <div className="space-y-2">
              <ChoiceLogoPicker
                mode="radio"
                options={inputOptions}
                logos={choiceOptionLogos}
                value={getDropdownFieldValue() ?? undefined}
                onChange={applyDropdownSelection}
                placeholder={input.input_title || "Select an option"}
                disabled={preview}
              />
              {renderDropdownOtherInput()}
            </div>
          );
        }

        return (
          <div className="space-y-2">
            {isMobile ? (
              <ChoicePopupPicker
                options={inputOptions}
                value={getDropdownFieldValue() ?? undefined}
                onChange={applyDropdownSelection}
                onClear={clearDropdownSelection}
                allowClear
                placeholder={input.input_title || "Select an option"}
                disabled={preview}
              />
            ) : (
              <Select
                allowClear
                size="large"
                className="w-full"
                placeholder={input.input_title || "Select an option"}
                value={getDropdownFieldValue() ?? undefined}
                popupClassName="select-dropdown-popup"
                popupMatchSelectWidth
                listHeight={320}
                placement="bottomLeft"
                dropdownRender={(menu) => <div className="py-1">{menu}</div>}
                onPopupScroll={(e) => e.stopPropagation()}
                getPopupContainer={() => document.body}
                onSelect={applyDropdownSelection}
                onClear={clearDropdownSelection}
                optionLabelProp="label"
                style={{ width: "100%" }}
              >
                {inputOptions.map((option, idx) => (
                  <Select.Option
                    key={idx}
                    value={option}
                    label={option}
                    className="!h-auto whitespace-normal py-2 leading-snug"
                  >
                    <span className="block whitespace-normal leading-snug">
                      {option}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            )}
            {renderDropdownOtherInput()}
          </div>
        );
      case "radio":
        if (useLogoOptionPicker) {
          return (
            <div className="space-y-2">
              <ChoiceLogoPicker
                mode="radio"
                options={inputOptions}
                logos={choiceOptionLogos}
                value={selectedOption}
                onChange={applyRadioSelection}
                placeholder={input.input_title || "Select an option"}
                disabled={preview}
              />

              {showOtherInput && (
                <Form.Item
                  label={
                    form.getFieldValue(["responses", index.toString()])
                      ?.selection || "Other"
                  }
                  labelCol={{ span: 24 }}
                  className="mb-0"
                  name={["responses", `${index}_other`]}
                  rules={[
                    {
                      required: preview ? false : (input.require ?? true),
                      message: "Please specify a value",
                      validator: async (_, value) => {
                        if (preview) return Promise.resolve();
                        if (input.require !== false && showOtherInput) {
                          if (!otherValue || otherValue.trim() === "") {
                            throw new Error("Please specify a value");
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  validateTrigger={["onChange", "onBlur"]}
                >
                  <Input
                    size="large"
                    className="h-11 rounded-xl"
                    placeholder="Please specify..."
                    value={otherValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOtherValue(value);

                      form.setFieldValue(["responses", index.toString()], {
                        selection: form.getFieldValue([
                          "responses",
                          index.toString(),
                        ])?.selection,
                        value: value,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-2">
            <div className="survey-choice-list-scroll">
            <Radio.Group
              className="flex w-full flex-col"
              value={selectedOption}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedOption(value);

                const isOther = value?.trim().toLowerCase() === "other";

                setShowOtherInput(isOther);

                if (isOther) {
                  setOtherValue("");

                  form.setFieldValue(["responses", index.toString()], {
                    selection: value,
                    otherValue: "",
                  });
                } else {
                  setOtherValue("");
                  form.setFieldValue(["responses", index.toString()], value);
                }
              }}
            >
              {inputOptions.map((option, idx) => (
                <Radio
                  key={idx}
                  value={option}
                  className="mb-4 w-full bg-white p-0 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  {renderChoiceOptionContent(option, idx)}
                </Radio>
              ))}
            </Radio.Group>
            </div>

            {showOtherInput && (
              <Form.Item
                label={
                  form.getFieldValue(["responses", index.toString()])
                    ?.selection || "Other"
                }
                labelCol={{ span: 24 }}
                className="mb-0"
                name={["responses", `${index}_other`]}
                rules={[
                  {
                    required: preview ? false : (input.require ?? true),
                    message: "Please specify a value",
                    validator: async (_, value) => {
                      if (preview) return Promise.resolve();
                      if (input.require !== false && showOtherInput) {
                        if (!otherValue || otherValue.trim() === "") {
                          throw new Error("Please specify a value");
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateTrigger={["onChange", "onBlur"]}
              >
                <Input
                  size="large"
                  className="h-11 rounded-xl"
                  placeholder="Please specify..."
                  value={otherValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setOtherValue(value);

                    form.setFieldValue(["responses", index.toString()], {
                      selection: form.getFieldValue([
                        "responses",
                        index.toString(),
                      ])?.selection,
                      value: value,
                    });
                  }}
                />
              </Form.Item>
            )}
          </div>
        );
      case "checkbox":
        const checkboxValues = normalizeCheckboxValues(selectedCheckboxes);
        if (useLogoOptionPicker) {
          return (
            <div className="space-y-2">
              <ChoiceLogoPicker
                mode="checkbox"
                options={inputOptions}
                logos={choiceOptionLogos}
                value={checkboxValues}
                onChange={applyCheckboxSelection}
                placeholder={input.input_title || "Select options"}
                disabled={preview}
              />
              {showOtherInput && (
                <Form.Item
                  label={
                    form.getFieldValue(["responses", index.toString()])
                      ?.selection || "Other"
                  }
                  labelCol={{ span: 24 }}
                  className="mb-0"
                  name={["responses", `${index}_other`]}
                  rules={[
                    {
                      required: preview ? false : (input.require ?? true),
                      message: "Please specify a value",
                      validator: async (_, value) => {
                        if (preview) return Promise.resolve();
                        if (input.require !== false && showOtherInput) {
                          if (
                            !checkboxOtherValue ||
                            checkboxOtherValue.trim() === ""
                          ) {
                            throw new Error("Please specify a value");
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  validateTrigger={["onChange", "onBlur"]}
                >
                  <Input
                    size="large"
                    className="h-11 rounded-xl"
                    placeholder="Please specify..."
                    value={checkboxOtherValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCheckboxOtherValue(value);

                      form.setFieldValue(["responses", index.toString()], {
                        selections: checkboxValues,
                        otherValue: value,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-2">
            <div className="survey-choice-list-scroll">
            <Checkbox.Group
              className="flex w-full flex-col"
              value={checkboxValues}
              onChange={(values) => {
                const nextValues = Array.isArray(values) ? values : [];
                setSelectedCheckboxes(
                  nextValues.length > 0 ? nextValues : null,
                );

                const hasOther = nextValues.some(
                  (v) => v?.trim().toLowerCase() === "other",
                );
                setShowOtherInput(hasOther);

                if (nextValues.length === 0) {
                  setCheckboxOtherValue("");
                  form.setFieldValue(["responses", index.toString()], null);
                  return;
                }

                if (hasOther) {
                  form.setFieldValue(["responses", index.toString()], {
                    selections: nextValues,
                    otherValue: checkboxOtherValue,
                  });
                } else {
                  setCheckboxOtherValue("");
                  form.setFieldValue(
                    ["responses", index.toString()],
                    nextValues,
                  );
                }
              }}
            >
              {inputOptions.map((option, idx) => (
                <Checkbox
                  key={idx}
                  value={option}
                  className="mb-4 w-full bg-white p-0 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  {renderChoiceOptionContent(option, idx)}
                </Checkbox>
              ))}
            </Checkbox.Group>
            </div>
            {showOtherInput && (
              <Form.Item
                label={
                  form.getFieldValue(["responses", index.toString()])
                    ?.selection || "Other"
                }
                labelCol={{ span: 24 }}
                className="mb-0"
                name={["responses", `${index}_other`]}
                rules={[
                  {
                    required: preview ? false : (input.require ?? true),
                    message: "Please specify a value",
                    validator: async (_, value) => {
                      if (preview) return Promise.resolve();
                      if (input.require !== false && showOtherInput) {
                        if (
                          !checkboxOtherValue ||
                          checkboxOtherValue.trim() === ""
                        ) {
                          throw new Error("Please specify a value");
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateTrigger={["onChange", "onBlur"]}
              >
                <Input
                  size="large"
                  className="h-11 rounded-xl"
                  placeholder="Please specify..."
                  value={checkboxOtherValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCheckboxOtherValue(value);

                    form.setFieldValue(["responses", index.toString()], {
                      selections: checkboxValues,
                      otherValue: value,
                    });
                  }}
                />
              </Form.Item>
            )}
          </div>
        );
      case "file":
        return (
          <FileUploadInput form={form} index={index} input={input} />
        );
      case "multiple-files":
        return (
          <FileUploadInput
            form={form}
            index={index}
            input={input}
            multiple
          />
        );
      case "signature":
        return (
          <Input
            size="large"
            className="h-11 rounded-xl"
            placeholder="First name Last name"
            disabled={preview}
          />
        );
      case "slider":
        return (
            <div className="space-y-4">
              <div className="border rounded-2xl p-2 sm:p-3 mt-2 sm:mt-0">
                <div className="flex flex-nowrap gap-1 sm:gap-2 justify-between">
                  {(() => {
                    const scaleType = parseInt(input.scale_type || "5");
                    const options =
                      inputOptions.length > 0
                        ? inputOptions
                        : Array(scaleType).fill("");

                    const showEmojis =
                      input.show_emojis ||
                      input.slider_config?.show_emojis ||
                      false;
                    const showStars =
                      input.show_stars ||
                      input.slider_config?.show_stars ||
                      false;

                    return options.map((rating, idx) => {
                      const value = (idx / (options.length - 1)) * 100;
                      const show_only_emojis = input.show_only_emojis;

                      const isStarFilled =
                        showStars &&
                        selectedRating !== null &&
                        idx <=
                          Math.round(
                            (selectedRating / 100) * (options.length - 1),
                          );

                      return (
                        <RatingOption
                          key={idx}
                          show_only_emojis={show_only_emojis}
                          value={rating}
                          label={rating}
                          index={idx}
                          showEmojis={showEmojis}
                          showStars={showStars}
                          totalOptions={options.length}
                          selected={selectedRating === value}
                          isStarFilled={isStarFilled}
                          onClick={() => {
                            const newValue = (idx / (options.length - 1)) * 100;
                            sliderInteractedRef.current = true;
                            Promise.resolve().then(() => {
                              setSliderValue(newValue);
                              setSelectedRating(newValue);

                              form.setFieldValue(
                                ["responses", index.toString()],
                                {
                                  value: newValue,
                                  label: rating,
                                  emojiIndex: idx,
                                  totalOptions: options.length,
                                },
                              );
                            });
                          }}
                        />
                      );
                    });
                  })()}
                </div>
                {(() => {
                  const showStars =
                    input.show_stars ||
                    input.slider_config?.show_stars ||
                    false;

                  if (showStars) return null;

                  return (
                    <div className="relative mt-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={sliderValue ?? 50}
                        step="0.1"
                        onPointerDown={() => {
                          sliderInteractedRef.current = true;
                        }}
                        onChange={(e) => {
                          if (!sliderInteractedRef.current) return;

                          const newValue = parseFloat(e.target.value);
                          const options =
                            inputOptions.length > 0
                              ? inputOptions
                              : Array(parseInt(input.scale_type || "5")).fill(
                                  "Option",
                                );
                          const totalSteps = options.length - 1;
                          const stepSize = 100 / totalSteps;
                          const nearestIndex = Math.round(newValue / stepSize);
                          const boundedIndex = Math.max(
                            0,
                            Math.min(nearestIndex, totalSteps),
                          );
                          const rating = options[boundedIndex];

                          Promise.resolve().then(() => {
                            setSliderValue(newValue);
                            setSelectedRating(newValue);

                            form.setFieldValue(
                              ["responses", index.toString()],
                              {
                                value: newValue,
                                label: rating,
                                emojiIndex: boundedIndex,
                                totalOptions: options.length,
                              },
                            );
                          });
                        }}
                        className={`w-full h-2 rounded-full appearance-none cursor-pointer transition-all ${
                          sliderValue == null ? "survey-slider-unselected" : ""
                        }`}
                        style={{
                          background:
                            sliderValue == null
                              ? "#E5E7EB"
                              : getSliderBackground(sliderValue),
                          WebkitAppearance: "none",
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
        );
      case "record-video":
        return (
          <VideoRecordingSection
            form={form}
            index={index}
            onVideoCapture={handleVideoCapture}
            recording_limit={input.recording_limit}
          />
        );

      default:
        return <Input placeholder="Type here..." type={input.input_type} />;
    }
  };

  useEffect(() => {
    const formValue = form.getFieldValue(["responses", index.toString()]);

    if (!formValue) {
      if (input.input_type === "checkbox") {
        setSelectedCheckboxes(null);
      }
      return;
    }

    const normalizedValues = normalizeCheckboxValues(formValue);
    setSelectedCheckboxes(
      normalizedValues.length > 0 ? normalizedValues : null,
    );
    setCheckboxOtherValue(formValue.otherValue || "");
    setShowOtherInput(
      normalizedValues.some((v) => v?.trim?.().toLowerCase() === "other"),
    );
  }, []);

  useEffect(() => {
    const formValue = form.getFieldValue(["responses", index.toString()]);

    if (!formValue) return;

    // Radio/dropdown responses can be either primitive option value or
    // object shape { selection, value/otherValue } when "Other" is chosen.
    if (input.input_type === "radio" || input.input_type === "dropdown") {
      if (typeof formValue === "object") {
        const selection = formValue.selection;
        setSelectedOption(selection);
        setOtherValue(formValue.value || formValue.otherValue || "");
        setShowOtherInput(selection?.trim?.().toLowerCase() === "other");
      } else {
        setSelectedOption(formValue);
        setShowOtherInput(formValue?.trim?.().toLowerCase() === "other");
      }
    }
  }, [form, index, input.input_type]);

  useEffect(() => {
    if (input.input_type !== "slider") return;

    const formValue = form.getFieldValue(["responses", index.toString()]);

    if (!hasExplicitSliderSelection(formValue)) {
      sliderInteractedRef.current = false;
      setSliderValue(null);
      setSelectedRating(null);
      return;
    }

    sliderInteractedRef.current = true;

    const restoredValue =
      typeof formValue === "object" && formValue.value != null
        ? parseFloat(formValue.value)
        : parseFloat(formValue);

    if (!Number.isNaN(restoredValue)) {
      setSliderValue(restoredValue);
      setSelectedRating(restoredValue);
    }
  }, []);

  return (
    <div className="mb-0 w-full min-w-0 max-w-full">
      <Form.Item
        label={input.input_title || null}
        name={["responses", index.toString()]}
        initialValue={null}
        rules={[
          {
            required: preview ? false : (input?.require ?? true),
            message: `Please ${
              input.input_type === "record-video"
                ? "record a BSL video"
                : input.input_type === "file" ||
                    input.input_type === "multiple-files"
                  ? "upload a file"
                  : input.input_type === "slider"
                    ? "select a rating"
                    : input.input_type === "checkbox"
                      ? "select at least one option"
                      : input.input_type === "radio"
                        ? "select an option"
                        : isRadioListInputType(input.input_type)
                          ? "select an option for every row"
                          : input.input_type === "signature"
                            ? "enter firstname lastname"
                          : input.input_type === "email"
                          ? "enter a valid email address"
                          : input.input_type === "name"
                            ? "enter your name"
                            : "provide a response"
            }`,
            validator: async (_, value) => {
              if (preview) return Promise.resolve();

              if (input.input_type === "signature" && value) {
                if (!isValidSignatureName(value)) {
                  throw new Error("Please enter fullname");
                }
              }

              if (!input.require) return Promise.resolve();
              if (input.input_type === "email" && value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                if (!emailRegex.test(value)) {
                  throw new Error("Please enter a valid email address");
                }
              }

              if (input.input_type === "record-video" && !value) {
                throw new Error("Please record a video before proceeding");
              }

              if (
                (input.input_type === "file" ||
                  input.input_type === "multiple-files") &&
                (!value || (Array.isArray(value) && value.length === 0))
              ) {
                throw new Error("Please upload a file before proceeding");
              }

              if (input.input_type === "checkbox") {
                const selections = Array.isArray(value)
                  ? value
                  : value?.selections;
                if (
                  value == null ||
                  (Array.isArray(selections) && selections.length === 0)
                ) {
                  throw new Error("Please select at least one option");
                }
              }

              if (input.input_type === "slider" && !value) {
                throw new Error("Please select a rating");
              }

              if (isRadioListRequiredEmpty(input, value)) {
                throw new Error("Please select an option for every row");
              }

              return Promise.resolve();
            },
          },
        ]}
        className="m-0 w-full min-w-0 max-w-full"
      >
        {renderInput()}
      </Form.Item>
    </div>
  );
};

export default DynamicInput;
